import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useMemo, lazy, Suspense } from 'react';
import { useAppSelector, useTenant, Tenant, useBrand, useNavbarVisibility } from '@/hooks';
import { selectCurrentStoreId } from '@/state/slices/storesSlice';
import { ProtectedRoute } from './ProtectedRoute';
import { Navbar, ScrollToTop } from '@/components/layout';

// Constants & Config
import { IS_MOBILE_APP } from '@/constants/env';

// Lazy-loaded pages - each becomes its own chunk
const LoginV2 = lazy(() => import('@/pages/auth/LoginV2'));
const Register = lazy(() => import('@/pages/auth/Register'));
const AllStoreDashboard = lazy(() => import('@/pages/dashboard/AllStoreDashboard'));
const StoreDashboard = lazy(() => import('@/pages/dashboard/StoreDashboard'));
const StoreDashboardV2 = lazy(() => import('@/pages/dashboard/StoreDashboardV2'));
const Inventory = lazy(() => import('@/pages/inventory/Inventory'));
const AddItem = lazy(() => import('@/pages/inventory/AddItem'));
const EditItem = lazy(() => import('@/pages/inventory/EditItem'));
const ItemDetail = lazy(() => import('@/pages/inventory/ItemDetail'));
const Invoices = lazy(() => import('@/pages/invoice/list/Invoices'));
const InvoiceDetails = lazy(() => import('@/pages/invoice/details/InvoiceDetails'));
const InvoiceView = lazy(() => import('@/pages/invoice/details/InvoiceView'));
const EditInvoice = lazy(() => import('@/pages/invoice/creation/EditInvoice'));
const CreateInvoice = lazy(() => import('@/pages/invoice/creation/CreateInvoice'));
const InvoiceReport = lazy(() => import('@/pages/invoice/reports/InvoiceReport'));
const InvoicePaymentPage = lazy(() => import('@/pages/invoice/payment/InvoicePaymentPage'));
const Profile = lazy(() => import('@/pages/settings/profile/Profile'));
const DeveloperSettings = lazy(() => import('@/pages/settings/developer/DeveloperSettings'));
const StoreSettings = lazy(() => import('@/pages/settings/store/StoreSettings'));
const PrinterSettings = lazy(() => import('@/pages/settings/printer/PrinterSettings'));
const OAuthSettings = lazy(() => import('@/pages/settings/oauth/OAuthSettings'));
const Customers = lazy(() => import('@/pages/customer/Customers'));
const CustomerDetails = lazy(() => import('@/pages/customer/CustomerDetails'));
const IntroPage = lazy(() => import('@/pages/intro/IntroPage'));
const Consent = lazy(() => import('@/pages/oauth/Consent'));
const AdminDashboard = lazy(() => import('@/pages/admin/dashboard/AdminDashboard'));
const AdminStoreDetail = lazy(() => import('@/pages/admin/store/AdminStoreDetail'));
const UsersInsights = lazy(() => import('@/pages/admin/insights/UsersInsights'));
const StoresInsights = lazy(() => import('@/pages/admin/insights/StoresInsights'));
const InvoicesInsights = lazy(() => import('@/pages/admin/insights/InvoicesInsights'));
const MunshiInsights = lazy(() => import('@/pages/admin/insights/MunshiInsights'));
const SpecialInsights = lazy(() => import('@/pages/admin/insights/SpecialInsights'));
const LedgerInsights = lazy(() => import('@/pages/admin/insights/LedgerInsights'));
const EventsInsights = lazy(() => import('@/pages/admin/insights/EventsInsights'));
const PosterTemplateManager = lazy(() => import('@/pages/admin/templates/PosterTemplateManager'));
const PosterTemplateList = lazy(() => import('@/pages/admin/templates/PosterTemplateList'));
const BuyerDashboard = lazy(() => import('@/pages/buyer/BuyerDashboard'));
const BuyerStores = lazy(() => import('@/pages/buyer/BuyerStores'));
const BuyerInvoices = lazy(() => import('@/pages/buyer/BuyerInvoices'));
const BuyerStoreDetails = lazy(() => import('@/pages/buyer/BuyerStoreDetails'));
const BuyerLedger = lazy(() => import('@/pages/buyer/BuyerLedger'));
const BuyerLedgerStore = lazy(() => import('@/pages/buyer/BuyerLedgerStore'));
const BuyerOrder = lazy(() => import('@/pages/buyer/BuyerOrder'));
const BuyerOrderTracker = lazy(() => import('@/pages/buyer/BuyerOrderTracker'));
const BuyerSelectStore = lazy(() => import('@/pages/buyer/BuyerSelectStore'));
const ScanBarcode = lazy(() => import('@/pages/tools/barcode/ScanBarcode'));
const PosterSharing = lazy(() => import('@/pages/tools/poster/PosterSharing'));
const PosterConfig = lazy(() => import('@/pages/tools/poster/PosterConfig'));
const OnlineStore = lazy(() => import('@/pages/tools/store/OnlineStore'));
const ImageToInvoice = lazy(() => import('@/pages/tools/conversion/ImageToInvoice'));
const Ledger = lazy(() => import('@/pages/ledger/list/Ledger'));
const CreateLedgerEntry = lazy(() => import('@/pages/ledger/creation/CreateLedgerEntry'));
const CustomerLedger = lazy(() => import('@/pages/ledger/customer/CustomerLedger'));
const LedgerReport = lazy(() => import('@/pages/ledger/reports/LedgerReport'));
const CustomerOrderDetails = lazy(() => import('@/pages/order/CustomerOrderDetails'));
const MunshiJiChat = lazy(() => import('@/pages/munshi/MunshiJiChat').then(m => ({ default: m.MunshiJiChat })));
const PrivacyPolicy = lazy(() => import('@/pages/public/PrivacyPolicy'));
const OurMission = lazy(() => import('@/pages/public/OurMission'));
const DownloadAPK = lazy(() => import('@/pages/public/DownloadAPK'));
const InstallApp = lazy(() => import('@/pages/public/InstallApp'));
const OpenApp = lazy(() => import('@/pages/public/OpenApp'));
const CustomerEngagement = lazy(() => import('@/pages/insights/CustomerEngagement'));
const InsightsHub = lazy(() => import('@/pages/insights/InsightsHub'));

export const AppRoutes = () => {
  const tenant = useTenant();
  const brand = useBrand();
  const { user, publicUser } = useAppSelector((state) => state.auth);
  const currentStoreId = useAppSelector(selectCurrentStoreId);
  const { isNavbarVisible } = useNavbarVisibility();
  const location = useLocation();

  /**
   * Determines the root route based on brand, tenant and user state
   * Memoized to prevent unnecessary recalculations on every render
   */
  const rootRoute = useMemo(() => {
    // Buyer tenant
    if (tenant === Tenant.Buyer) {
      if (user) {
        return <BuyerDashboard />;
      }
      return IS_MOBILE_APP ? <LoginV2 /> : <IntroPage />;
    }
    
    // Zeus tenant (admin)
    if (tenant === Tenant.Zeus) {
      if (publicUser?.is_superadmin) {
        return <ProtectedRoute><AdminDashboard /></ProtectedRoute>;
      }
      return IS_MOBILE_APP ? <LoginV2 /> : <IntroPage />;
    }
    
    // Seller tenant (default, includes all brands like Munshiji and Kirana360)
    if (tenant === Tenant.Seller) {
      if (user) {
        if (currentStoreId) {
          return <StoreDashboardV2 storeIdOverride={currentStoreId} />;
        }
        return <AllStoreDashboard />;
      }
      return IS_MOBILE_APP ? <LoginV2 /> : <IntroPage />;
    }
    
    // Fallback
    return IS_MOBILE_APP ? <LoginV2 /> : <IntroPage />;
  }, [tenant, brand.mode, user, publicUser?.is_superadmin, currentStoreId]);

  // Show full routing for all brands
  return (
    <div className="absolute inset-0 flex flex-col bg-gray-50">
      {/* Navbar */}
      {isNavbarVisible() && <Navbar />}
      
      {/* Main content - scrollable area */}
      <main className={`${location.pathname === '/munshi-chat' ? '' : 'container mx-auto'} flex-1 overflow-y-auto ${ location.pathname === '/intro' || location.pathname === '/login' || location.pathname === '/munshi-chat' || (location.pathname === '/' && !user) ? 'px-0 py-0' : 'px-3 sm:px-4 py-4 sm:py-8'}`}>
        <ScrollToTop />
        <Suspense fallback={<div className="flex items-center justify-center h-full"><div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" /></div>}>
        <Routes>
          {/* =============================================================================
              ROOT ROUTE - Tenant-aware routing
              ============================================================================= */}
          <Route path="/" element={rootRoute} />

          {/* =============================================================================
              COMMON ROUTES - Available to all users
              ============================================================================= */}
          <Route path="/login" element={<LoginV2 />} />
          <Route path="/register" element={<Register />} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/settings/developer" element={<ProtectedRoute><DeveloperSettings /></ProtectedRoute>} />
          <Route path="/settings/oauth" element={<ProtectedRoute><OAuthSettings /></ProtectedRoute>} />
          <Route path="/oauth/consent" element={<ProtectedRoute><Consent /></ProtectedRoute>} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />

          {/* =============================================================================
              PUBLIC INVOICE ROUTES - Accessible by all users
              ============================================================================= */}
          <Route path="/stores/:storeId/invoices/:invoiceId/view" element={<InvoiceView />} />
          <Route path="/stores/:id/invoices/:invoiceId/payment" element={<InvoicePaymentPage />} />

          {/* =============================================================================
              ADMIN ROUTES - Zeus tenant with superadmin access
              ============================================================================= */}
          {tenant === Tenant.Zeus && publicUser?.is_superadmin && (
            <>
              <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/invoices-insights" element={<ProtectedRoute><InvoicesInsights /></ProtectedRoute>} />
              <Route path="/admin/stores-insights" element={<ProtectedRoute><StoresInsights /></ProtectedRoute>} />
              <Route path="/admin/ledger-insights" element={<ProtectedRoute><LedgerInsights /></ProtectedRoute>} />
              <Route path="/admin/events-insights" element={<ProtectedRoute><EventsInsights /></ProtectedRoute>} />
              <Route path="/admin/stores/:storeId" element={<ProtectedRoute><AdminStoreDetail /></ProtectedRoute>} />
              <Route path="/admin/stores/:storeId/settings" element={<ProtectedRoute><StoreSettings /></ProtectedRoute>} />
              <Route path="/admin/users-insights" element={<ProtectedRoute><UsersInsights /></ProtectedRoute>} />
              <Route path="/admin/munshi" element={<ProtectedRoute><MunshiInsights /></ProtectedRoute>} />
              <Route path="/admin/special" element={<ProtectedRoute><SpecialInsights /></ProtectedRoute>} />
              <Route path="/admin/poster-templates" element={<ProtectedRoute><PosterTemplateList /></ProtectedRoute>} />
              <Route path="/admin/poster-templates/new" element={<ProtectedRoute><PosterTemplateManager /></ProtectedRoute>} />
              <Route path="/admin/poster-templates/:id" element={<ProtectedRoute><PosterTemplateManager /></ProtectedRoute>} />
            </>
          )}

          {/* =============================================================================
              SELLER ROUTES - Store management and operations
              ============================================================================= */}
          {tenant === Tenant.Seller && (
            <>
              {/* Store Management */}
              <Route path="/stores" element={<ProtectedRoute><AllStoreDashboard /></ProtectedRoute>} />
              <Route path="/stores/:id" element={<ProtectedRoute><StoreDashboardV2 /></ProtectedRoute>} />
              <Route path="/dashboard-old" element={<ProtectedRoute><StoreDashboard /></ProtectedRoute>} />
              <Route path="/dashboard-old/:id" element={<ProtectedRoute><StoreDashboard /></ProtectedRoute>} />
              <Route path="/dashboard-new" element={<ProtectedRoute><StoreDashboardV2 /></ProtectedRoute>} />
              <Route path="/dashboard-new/:id" element={<ProtectedRoute><StoreDashboardV2 /></ProtectedRoute>} />
  
              {/* Inventory Management */}
              <Route path="/stores/:id/inventory" element={<ProtectedRoute><Inventory /></ProtectedRoute>} />
              <Route path="/stores/:id/inventory/add" element={<ProtectedRoute><AddItem /></ProtectedRoute>} />
              <Route path="/stores/:id/inventory/edit/:itemId" element={<ProtectedRoute><EditItem /></ProtectedRoute>} />
              <Route path="/stores/:id/inventory/item/:itemId" element={<ProtectedRoute><ItemDetail /></ProtectedRoute>} />
              
              {/* Invoice Management */}
              <Route path="/stores/:id/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
              <Route path="/stores/:id/invoices/:invoiceId" element={<ProtectedRoute><InvoiceDetails /></ProtectedRoute>} />
              <Route path="/stores/:id/invoices/:invoiceId/edit" element={<ProtectedRoute><EditInvoice /></ProtectedRoute>} />
              <Route path="/stores/:id/create-invoice" element={<ProtectedRoute><CreateInvoice /></ProtectedRoute>} />
              
              {/* Customer Management */}
              <Route path="/stores/:id/customers" element={<ProtectedRoute><Customers /></ProtectedRoute>} />
              <Route path="/stores/:id/customers/id/:customerId" element={<ProtectedRoute><CustomerDetails /></ProtectedRoute>} />
              <Route path="/stores/:id/customers/phone/:customerPhone" element={<ProtectedRoute><CustomerDetails /></ProtectedRoute>} />
              
              {/* Store Settings & Analytics */}
              <Route path="/stores/:id/settings" element={<ProtectedRoute><StoreSettings /></ProtectedRoute>} />
              <Route path="/stores/:id/settings/printer" element={<ProtectedRoute><PrinterSettings /></ProtectedRoute>} />
              <Route path="/stores/:id/insights" element={<ProtectedRoute><InsightsHub /></ProtectedRoute>} />
              <Route path="/stores/:id/invoice-report" element={<ProtectedRoute><InvoiceReport /></ProtectedRoute>} />
              <Route path="/stores/:id/customer-engagement" element={<ProtectedRoute><CustomerEngagement /></ProtectedRoute>} />
              
              {/* Ledger Management */}
              <Route path="/stores/:id/ledger" element={<ProtectedRoute><Ledger /></ProtectedRoute>} />
              <Route path="/stores/:id/ledger/create" element={<ProtectedRoute><CreateLedgerEntry /></ProtectedRoute>} />
              <Route path="/stores/:storeId/ledger/:customerPhone" element={<ProtectedRoute><CustomerLedger /></ProtectedRoute>} />
              
              {/* Tools */}
              <Route path="/tools/scan-barcode" element={<ProtectedRoute><ScanBarcode /></ProtectedRoute>} />
              <Route path="/stores/:id/tools/poster-sharing" element={<ProtectedRoute><PosterSharing /></ProtectedRoute>} />
              <Route path="/stores/:id/tools/poster-config" element={<ProtectedRoute><PosterConfig /></ProtectedRoute>} />
              <Route path="/stores/:id/tools/online-store" element={<ProtectedRoute><OnlineStore /></ProtectedRoute>} />
              <Route path="/stores/:id/tools/image-to-invoice" element={<ProtectedRoute><ImageToInvoice /></ProtectedRoute>} />
              <Route path="/stores/:id/ledger/report" element={<ProtectedRoute><LedgerReport /></ProtectedRoute>} />
              
              {/* Customer Orders */}
              <Route path="/stores/:id/customer-orders/:orderId" element={<ProtectedRoute><CustomerOrderDetails /></ProtectedRoute>} />
              
              {/* Munshi Ji AI Chat */}
              <Route path="/munshi-chat" element={<ProtectedRoute><MunshiJiChat /></ProtectedRoute>} />
            </>
          )}

          {/* =============================================================================
              BUYER ROUTES - Purchase and invoice viewing
              ============================================================================= */}
          {tenant === Tenant.Buyer && (
            <>
              <Route path="/buyer/stores" element={<ProtectedRoute><BuyerStores /></ProtectedRoute>} />
              <Route path="/buyer/stores/:storeId" element={<BuyerStoreDetails />} />
              <Route path="/buyer/select-store-for-order" element={<ProtectedRoute><BuyerSelectStore /></ProtectedRoute>} />
              <Route path="/buyer/order/:storeId" element={<ProtectedRoute><BuyerOrder /></ProtectedRoute>} />
              <Route path="/buyer/orders/:orderId/tracker" element={<ProtectedRoute><BuyerOrderTracker /></ProtectedRoute>} />
              <Route path="/buyer/invoices" element={<ProtectedRoute><BuyerInvoices /></ProtectedRoute>} />
              <Route path="/buyer/ledger" element={<BuyerLedger />} />
              <Route path="/buyer/ledger/store/:storeid" element={<BuyerLedgerStore />} />
              <Route path="/buyer/tools/poster-sharing" element={<ProtectedRoute><PosterSharing /></ProtectedRoute>} />
              <Route path="/buyer/tools/poster-config" element={<ProtectedRoute><PosterConfig /></ProtectedRoute>} />
            </>
          )}

          {/* =============================================================================
              PUBLIC ROUTES - No authentication required
              ============================================================================= */}
          {/* Public store route - works without login and in any tenant */}
          <Route path="/store/:storeId" element={<BuyerStoreDetails />} />
          <Route path="/our-mission" element={<OurMission />} />
          <Route path="/intro" element={<IntroPage />} />
          <Route path="/install" element={<InstallApp />} />
          <Route path="/download-app" element={<DownloadAPK />} />
          <Route path="/app" element={<OpenApp />} />

          {/* =============================================================================
              CATCH ALL - Redirect to root
              ============================================================================= */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </main>
    </div>
  );
}; 